/*
 * Questionnaire definition for the penetration testing request form.
 *
 * Three pages, eight questions each. Labels carry en/ja; any other language
 * falls back to en, matching the behaviour of the translation store.
 */

const opt = (v, en, ja) => ({v, en, ja}),

    // same, with a localized description shown on hover
    optd = (v, en, ja, altEn, altJa) => ({v, en, ja, "alt": {"en": altEn, "ja": altJa}});

const PAGES = [
    {
        "key": "profile",
        "title": {"en": "Organization profile", "ja": "組織プロフィール"},
        "blurb": {
            "en": "Your organization and the information it holds. Approximate ranges are sufficient throughout — no figure here needs to be exact.",
            "ja": "貴組織の概要と、保有されている情報についてお伺いします。いずれの設問も、おおよその目安でご回答いただければ問題ございません。"
        },
        "questions": [
            {
                "key": "contact",
                "type": "fields",
                "label": {"en": "Point of contact", "ja": "ご担当者情報"},
                "fields": [
                    {"key": "name", "type": "text", "required": true,
                        "label": {"en": "Name", "ja": "お名前"}},
                    {"key": "email", "type": "email", "required": true,
                        "label": {"en": "Work email", "ja": "会社メールアドレス"}},
                    {"key": "role", "type": "text",
                        "label": {"en": "Role", "ja": "役職"}}
                ]
            },
            {
                "key": "org",
                "type": "fields",
                "label": {"en": "Organization", "ja": "組織情報"},
                "fields": [
                    {"key": "company", "type": "text", "required": true,
                        "label": {"en": "Organization name", "ja": "組織名"}},
                    {"key": "domain", "type": "text",
                        "label": {"en": "Primary domain", "ja": "主要ドメイン"}}
                ]
            },
            {
                "key": "industry",
                "type": "select",
                "label": {"en": "Industry sector", "ja": "業種"},
                "options": [
                    opt("saas", "Technology / SaaS", "IT・SaaS"),
                    opt("telecom", "Telecommunications", "通信"),
                    opt("finance", "Financial services / banking", "金融・銀行"),
                    opt("insurance", "Insurance", "保険"),
                    opt("crypto", "Crypto / Web3", "暗号資産・Web3"),
                    opt("healthcare", "Healthcare / hospitals", "医療・病院"),
                    opt("pharma", "Pharmaceuticals / biotech", "製薬・バイオテクノロジー"),
                    opt("meddevice", "Medical devices", "医療機器"),
                    opt("defence", "Aerospace & defence", "航空宇宙・防衛"),
                    opt("manufacturing", "Manufacturing / industrial", "製造・産業"),
                    opt("heavy", "Heavy industry and engineering", "重工業・プラント"),
                    opt("automotive", "Automotive", "自動車"),
                    opt("chemicals", "Chemicals", "化学"),
                    opt("energy", "Energy / utilities", "エネルギー・電力ガス"),
                    opt("mining", "Mining / natural resources", "鉱業・資源"),
                    opt("construction", "Construction / real estate", "建設・不動産"),
                    opt("retail", "Retail / e-commerce", "小売・EC"),
                    opt("consumer", "Consumer goods", "消費財"),
                    opt("food", "Food, beverage & agriculture", "食品・飲料・農業"),
                    opt("rail", "Rail and public transport", "鉄道・公共交通"),
                    opt("aviation", "Aviation / airlines", "航空・エアライン"),
                    opt("logistics", "Logistics and freight", "物流・貨物輸送"),
                    opt("maritime", "Maritime / shipping", "海運・船舶"),
                    opt("travel", "Hospitality / travel", "ホスピタリティ・観光"),
                    opt("media", "Media / publishing", "メディア・出版"),
                    opt("entertainment", "Entertainment / gaming", "エンターテインメント・ゲーム"),
                    opt("education", "Education and academic institutions", "教育・学術機関"),
                    opt("public", "Public sector / government", "官公庁・自治体"),
                    opt("nonprofit", "Non-profit / NGO", "非営利団体・NGO"),
                    opt("legal", "Legal services", "法務サービス"),
                    opt("services", "Professional services / consulting", "専門サービス・コンサルティング"),
                    opt("other", "Other", "その他")
                ]
            },
            {
                "key": "geography",
                "type": "selects",
                "label": {"en": "Base and scale of operations", "ja": "拠点および事業規模"},
                "fields": [
                    {"key": "hq", "label": {"en": "Headquarters", "ja": "本社所在国"},
                        "options": [
                            opt("jp", "Japan", "日本"),
                            opt("ar", "Argentina", "アルゼンチン"),
                            opt("au", "Australia", "オーストラリア"),
                            opt("at", "Austria", "オーストリア"),
                            opt("bh", "Bahrain", "バーレーン"),
                            opt("bd", "Bangladesh", "バングラデシュ"),
                            opt("be", "Belgium", "ベルギー"),
                            opt("br", "Brazil", "ブラジル"),
                            opt("ca", "Canada", "カナダ"),
                            opt("cl", "Chile", "チリ"),
                            opt("cn", "China", "中国"),
                            opt("co", "Colombia", "コロンビア"),
                            opt("cr", "Costa Rica", "コスタリカ"),
                            opt("hr", "Croatia", "クロアチア"),
                            opt("cz", "Czechia", "チェコ"),
                            opt("dk", "Denmark", "デンマーク"),
                            opt("eg", "Egypt", "エジプト"),
                            opt("ee", "Estonia", "エストニア"),
                            opt("fi", "Finland", "フィンランド"),
                            opt("fr", "France", "フランス"),
                            opt("de", "Germany", "ドイツ"),
                            opt("gr", "Greece", "ギリシャ"),
                            opt("hk", "Hong Kong", "香港"),
                            opt("hu", "Hungary", "ハンガリー"),
                            opt("in", "India", "インド"),
                            opt("id", "Indonesia", "インドネシア"),
                            opt("ie", "Ireland", "アイルランド"),
                            opt("il", "Israel", "イスラエル"),
                            opt("it", "Italy", "イタリア"),
                            opt("ke", "Kenya", "ケニア"),
                            opt("kw", "Kuwait", "クウェート"),
                            opt("lv", "Latvia", "ラトビア"),
                            opt("lt", "Lithuania", "リトアニア"),
                            opt("lu", "Luxembourg", "ルクセンブルク"),
                            opt("my", "Malaysia", "マレーシア"),
                            opt("mx", "Mexico", "メキシコ"),
                            opt("ma", "Morocco", "モロッコ"),
                            opt("nl", "Netherlands", "オランダ"),
                            opt("nz", "New Zealand", "ニュージーランド"),
                            opt("ng", "Nigeria", "ナイジェリア"),
                            opt("no", "Norway", "ノルウェー"),
                            opt("pk", "Pakistan", "パキスタン"),
                            opt("pa", "Panama", "パナマ"),
                            opt("pe", "Peru", "ペルー"),
                            opt("ph", "Philippines", "フィリピン"),
                            opt("pl", "Poland", "ポーランド"),
                            opt("pt", "Portugal", "ポルトガル"),
                            opt("qa", "Qatar", "カタール"),
                            opt("ro", "Romania", "ルーマニア"),
                            opt("sa", "Saudi Arabia", "サウジアラビア"),
                            opt("sg", "Singapore", "シンガポール"),
                            opt("sk", "Slovakia", "スロバキア"),
                            opt("si", "Slovenia", "スロベニア"),
                            opt("za", "South Africa", "南アフリカ"),
                            opt("kr", "South Korea", "韓国"),
                            opt("es", "Spain", "スペイン"),
                            opt("lk", "Sri Lanka", "スリランカ"),
                            opt("se", "Sweden", "スウェーデン"),
                            opt("ch", "Switzerland", "スイス"),
                            opt("tw", "Taiwan", "台湾"),
                            opt("th", "Thailand", "タイ"),
                            opt("tr", "Turkey", "トルコ"),
                            opt("ua", "Ukraine", "ウクライナ"),
                            opt("ae", "United Arab Emirates", "アラブ首長国連邦"),
                            opt("gb", "United Kingdom", "英国"),
                            opt("us", "United States", "米国"),
                            opt("uy", "Uruguay", "ウルグアイ"),
                            opt("vn", "Vietnam", "ベトナム"),
                            opt("other", "Other", "その他")
                        ]},
                    {"key": "sites", "label": {"en": "Offices and sites", "ja": "拠点数"},
                        "options": [
                            opt("1", "1", "1拠点"),
                            opt("2-5", "2–5", "2〜5拠点"),
                            opt("6-20", "6–20", "6〜20拠点"),
                            opt("20+", "More than 20", "20拠点超")
                        ]}
                ]
            },
            {
                "key": "sanctioned",
                "type": "chips",
                "label": {"en": "Offices or operations in specific overseas regions (optional)",
                    "ja": "海外特定地域における拠点・事業の有無（任意）"},
                "hint": {"en": "Operating in certain countries and regions carries a higher risk of being targeted by state-backed threat groups (APTs). We ask in order to design the most appropriate threat scenarios for your assessment.",
                    "ja": "一部の国や地域における事業展開は、国家背景を持つ攻撃グループ（APT）の標的となるリスクが高いため、最適な脅威シナリオを設計するための参考としてお伺いします。"},
                "options": [
                    opt("none", "No operations in such regions", "該当する地域での事業なし"),
                    opt("china", "Mainland China", "中国本土"),
                    opt("russia", "Russia or CIS", "ロシア・CIS諸国"),
                    opt("iran", "Iran", "イラン"),
                    opt("sanctioned", "A sanctioned jurisdiction", "制裁対象地域"),
                    opt("conflict", "A conflict-affected region", "紛争の影響を受ける地域"),
                    opt("unsure", "Not sure", "わからない"),
                    opt("private", "Prefer not to say", "回答を控える")
                ]
            },
            {
                "key": "employees",
                "type": "radio",
                "label": {"en": "Workforce size", "ja": "従業員数"},
                "options": [
                    opt("1-20", "1–20", "1〜20名"),
                    opt("21-100", "21–100", "21〜100名"),
                    opt("101-1000", "101–1,000", "101〜1,000名"),
                    opt("1001-5000", "1,001–5,000", "1,001〜5,000名"),
                    opt("5001-10000", "5,001–10,000", "5,001〜10,000名"),
                    opt("10000+", "More than 10,000", "10,000名超"),
                    opt("private", "Prefer not to say", "回答を控える")
                ]
            },
            {
                "key": "compliance",
                "type": "chips",
                "label": {"en": "Compliance certifications your organization is undertaking or has completed",
                    "ja": "取得済みまたは取得を進めている認証・規格"},
                "options": [
                    opt("iso27001", "ISO 27001 / ISMS", "ISO 27001／ISMS"),
                    opt("pmark", "PrivacyMark (P-Mark)", "プライバシーマーク"),
                    opt("ismap", "ISMAP", "ISMAP"),
                    opt("soc2", "SOC 1 / SOC 2", "SOC 1／SOC 2"),
                    opt("pci", "PCI DSS", "PCI DSS"),
                    opt("privacylaw", "Overseas data protection laws (GDPR, CCPA and similar)", "海外の個人情報保護法（GDPR／CCPA等）"),
                    opt("hipaa", "HIPAA", "HIPAA"),
                    opt("fisc", "FISC", "FISC安全対策基準"),
                    opt("mhlw", "Three Ministries (healthcare)", "医療情報3省2ガイドライン"),
                    opt("nist", "NIST CSF / CIS Controls", "NIST CSF／CIS Controls"),
                    opt("customer", "Customer-imposed requirements", "顧客要求事項"),
                    opt("none", "None / not sure", "なし・不明"),
                    opt("other", "Other", "その他"),
                    opt("private", "Prefer not to say", "回答を控える")
                ]
            },
            {
                "key": "data",
                "type": "chips",
                "label": {"en": "Categories of sensitive data held", "ja": "保有する機微情報の区分"},
                "options": [
                    opt("customer_pii", "Customer PII", "顧客の個人情報"),
                    opt("employee_pii", "Employee PII", "従業員の個人情報"),
                    opt("card", "Payment card data (in scope for PCI DSS)", "クレジットカード情報（PCI DSS対象）"),
                    opt("financial", "Banking and financial data (accounts, transaction records)", "金融・決済情報（銀行口座・取引データ等）"),
                    opt("health", "Medical / health data", "医療・健康情報"),
                    opt("biometric", "Biometric data", "生体情報"),
                    opt("government", "Government or defence data (including contracted work)", "政府・防衛関連の情報（受託案件等を含む）"),
                    opt("ip", "Intellectual property and trade secrets (proprietary source code, design drawings)", "知的財産・営業秘密（独自のソースコード、設計図面等）"),
                    opt("location", "Location data", "位置情報"),
                    opt("none", "No sensitive data", "機微情報なし"),
                    opt("other", "Other", "その他"),
                    opt("unknown", "Unknown", "不明"),
                    opt("private", "Prefer not to say", "回答を控える")
                ]
            },
            {
                "key": "exposure",
                "type": "chips",
                "label": {"en": "Organizational characteristics relevant to threat exposure",
                    "ja": "想定される脅威に関わる組織特性"},
                "hint": {"en": "Different organizational characteristics attract different types of adversary. Selecting those that apply allows us to design the assessment around threat scenarios specific to your organization rather than generic ones.",
                    "ja": "組織の特性によって、想定される攻撃者のタイプは異なります。該当する項目をお選びいただくことで、一般的な内容ではなく、貴組織に即した脅威シナリオに基づく診断設計が可能になります。"},
                "options": [
                    opt("critical", "Operator of critical infrastructure or essential services (power, finance, telecoms, healthcare, transport)", "重要インフラ・基幹サービスの運営事業者（電力、金融、通信、医療、交通等）"),
                    opt("gov", "Government or defence contracts", "政府・防衛関連の受託業務"),
                    opt("supplier", "Provides professional or IT services to a high-value target (government, critical infrastructure or similar)", "重要度の高い組織（政府機関・重要インフラ事業者等）への専門サービス・ITサービスの提供"),
                    opt("custodian", "Custodian of client or partner data", "顧客・提携先データの受託保管"),
                    opt("payments", "High-value payment or funds-transfer flows", "高額な決済・送金フローの取り扱い"),
                    opt("crypto", "Engages in or custodies cryptocurrency", "暗号資産の取り扱い・保管（カストディ）"),
                    opt("research", "Proprietary research, formulations or manufacturing know-how", "独自の研究開発・製法・製造ノウハウ"),
                    opt("consumer", "Consumer-facing brand with public recognition", "一般消費者に認知されたブランド"),
                    opt("listed", "Publicly listed company", "上場企業"),
                    opt("execs", "Publicly prominent executives or founders", "社会的に知名度の高い経営陣・創業者"),
                    opt("geopolitical", "Operations in geopolitically sensitive markets", "地政学的に機微な市場での事業展開"),
                    opt("none", "None of the above", "該当なし"),
                    opt("other", "Other", "その他"),
                    opt("private", "Prefer not to say", "回答を控える")
                ]
            }
        ]
    },
    {
        "key": "surface",
        "title": {"en": "Environment and attack surface", "ja": "環境およびアタックサーフェス（攻撃対象領域）"},
        "blurb": {
            "en": "The systems and platforms that make up your IT environment, and the external assets exposed to the internet. Please select all that apply.",
            "ja": "貴組織のIT環境を構成するシステム基盤と、インターネットに公開されている外部資産についてお伺いします。該当する項目をすべてご選択ください。"
        },
        "questions": [
            {
                "key": "users",
                "type": "radio",
                "label": {"en": "Users with system access", "ja": "システム利用者数"},
                "hint": {"en": "Not only your own employees, but also contractor and partner personnel to whom you have issued accounts.",
                    "ja": "貴組織の従業員だけでなく、アカウントを付与している委託先・パートナー企業の要員も含みます。"},
                "options": [
                    opt("under50", "Fewer than 50", "50名未満"),
                    opt("50-200", "50–200", "50〜200名"),
                    opt("201-1000", "201–1,000", "201〜1,000名"),
                    opt("1001-5000", "1,001–5,000", "1,001〜5,000名"),
                    opt("5001-10000", "5,001–10,000", "5,001〜10,000名"),
                    opt("10000+", "More than 10,000", "10,000名超"),
                    opt("unknown", "Unknown", "不明"),
                    opt("private", "Prefer not to say", "回答を控える")
                ]
            },
            {
                "key": "endpoints",
                "type": "chips",
                "label": {"en": "Endpoint platforms", "ja": "端末プラットフォーム"},
                "options": [
                    opt("windows", "Windows", "Windows"),
                    opt("macos", "macOS", "macOS"),
                    opt("linux", "Linux", "Linux"),
                    opt("ios", "iPhone / iPad", "iPhone／iPad"),
                    opt("android", "Android", "Android"),
                    opt("company", "Company-issued devices only", "会社支給端末のみ"),
                    opt("personal", "Personal devices used for business purposes", "私物端末の業務利用"),
                    opt("unmanaged", "Unmanaged devices present", "管理対象外の端末が存在"),
                    opt("other", "Other", "その他"),
                    opt("unknown", "Unknown", "不明")
                ]
            },
            {
                "key": "mdm",
                "type": "chips",
                "label": {"en": "Device management", "ja": "端末管理"},
                "options": [
                    opt("intune", "Microsoft Intune", "Microsoft Intune"),
                    opt("jamf", "Jamf", "Jamf"),
                    opt("workspaceone", "VMware Workspace ONE", "VMware Workspace ONE"),
                    opt("google", "Google Endpoint Management", "Google エンドポイント管理"),
                    opt("other", "Other", "その他"),
                    opt("none", "Not centrally managed", "集中管理していない"),
                    opt("unknown", "Unknown", "不明")
                ]
            },
            {
                "key": "identity",
                "type": "chips",
                "label": {"en": "Identity and productivity platforms", "ja": "ID基盤・業務プラットフォーム"},
                "options": [
                    opt("m365", "Microsoft 365", "Microsoft 365"),
                    opt("workspace", "Google Workspace", "Google Workspace"),
                    opt("entra", "Microsoft Entra ID", "Microsoft Entra ID"),
                    opt("ad", "Active Directory (on-premises)", "Active Directory（オンプレミス）"),
                    opt("okta", "Okta", "Okta"),
                    opt("ping", "Ping Identity", "Ping Identity"),
                    opt("google_id", "Google Identity", "Google Identity"),
                    opt("selfhosted", "Self-hosted mail / collaboration", "自社運用のメール・グループウェア"),
                    opt("sso", "Single sign-on across principal business applications", "主要な業務アプリでSSO導入済み"),
                    opt("mfa", "Multi-factor authentication enforced", "多要素認証を必須化"),
                    opt("none", "No central identity platform", "統合ID基盤なし"),
                    opt("other", "Other", "その他")
                ]
            },
            {
                "key": "cloud",
                "type": "chips",
                "label": {"en": "Cloud and hosting infrastructure", "ja": "クラウド・ホスティング基盤"},
                "options": [
                    opt("aws", "AWS", "AWS"),
                    opt("azure", "Microsoft Azure", "Microsoft Azure"),
                    opt("gcp", "Google Cloud", "Google Cloud"),
                    opt("oracle", "Oracle Cloud", "Oracle Cloud"),
                    opt("alibaba", "Alibaba Cloud", "Alibaba Cloud"),
                    opt("sakura", "Domestic cloud or VPS (Sakura, IIJ, NTT and similar)", "さくら、IIJ、NTT等の国内クラウド・VPS"),
                    opt("private", "Private cloud or hosting environment", "プライベートクラウド・ホスティング環境"),
                    opt("onprem", "On-premises servers in your own offices", "自社オフィス内のオンプレミスサーバー"),
                    opt("colo", "Data centre use or colocation", "データセンター利用・コロケーション"),
                    opt("none", "No public cloud (on-premises and data centre only)", "パブリッククラウドの利用なし（オンプレミス・DCのみ）"),
                    opt("other", "Other", "その他")
                ]
            },
            {
                "key": "footprint",
                "type": "selects",
                "label": {"en": "Internet-facing assets", "ja": "インターネット公開資産"},
                "hint": {"en": "Please select the approximate total number of systems and devices reachable from the internet. An exact figure is not required.",
                    "ja": "インターネットからアクセス可能な、システムや機器のおおよその総数をお選びください。正確な数値でなくても（概算で）差し支えありません。"},
                "fields": [
                    {"key": "domains", "label": {"en": "Domains and subdomains", "ja": "ドメイン・サブドメイン"},
                        "options": [
                            opt("1-5", "1–5", "1〜5"),
                            opt("6-25", "6–25", "6〜25"),
                            opt("26-100", "26–100", "26〜100"),
                            opt("100+", "More than 100", "100超"),
                            opt("unknown", "Unknown", "不明")
                        ]},
                    {"key": "apps", "label": {"en": "Public-facing applications", "ja": "公開アプリケーション"},
                        "options": [
                            opt("0-2", "0–2", "0〜2"),
                            opt("3-10", "3–10", "3〜10"),
                            opt("11-50", "11–50", "11〜50"),
                            opt("50+", "More than 50", "50超"),
                            opt("unknown", "Unknown", "不明")
                        ]},
                    {"key": "workloads", "label": {"en": "Servers and cloud workloads", "ja": "サーバー・クラウドワークロード"},
                        "options": [
                            opt("under25", "Fewer than 25", "25未満"),
                            opt("25-100", "25–100", "25〜100"),
                            opt("101-500", "101–500", "101〜500"),
                            opt("500+", "More than 500", "500超"),
                            opt("unknown", "Unknown", "不明")
                        ]}
                ]
            },
            {
                "key": "special",
                "type": "chips",
                "label": {"en": "Specialised environments", "ja": "特殊な環境"},
                "options": [
                    opt("ot", "OT / industrial control systems", "OT・産業制御システム"),
                    opt("medical", "Medical devices", "医療機器"),
                    opt("iot", "IoT / connected devices", "IoT機器"),
                    opt("k8s", "Kubernetes / containers", "Kubernetes・コンテナ"),
                    opt("mobile", "Mobile applications published by your organization", "自社が公開するモバイルアプリケーション"),
                    opt("vpn", "VPN and remote access environment", "VPN・リモートアクセス環境"),
                    opt("envs", "Separation of development, staging and production environments (tick if in place)", "開発・検証・本番環境の分離（対策済みの場合にチェック）"),
                    opt("none", "None of these", "該当なし"),
                    opt("other", "Other", "その他")
                ]
            },
            {
                "key": "ai",
                "type": "chips",
                "label": {"en": "AI systems in use", "ja": "AIシステムの利用状況"},
                "hint": {"en": "The attack surface specific to AI systems is assessed as a distinct area, so please indicate your current usage.",
                    "ja": "AIシステム特有のアタックサーフェスは独立した診断領域として評価するため、現在の運用状況についてお選びください。"},
                "options": [
                    opt("public", "Public generative-AI services (ChatGPT, Gemini, Claude and similar)", "一般提供の生成AIサービス（ChatGPT、Gemini、Claude 等）"),
                    opt("internal", "Internal chatbots or copilots (M365 Copilot and similar)", "社内チャットボット・コパイロット（M365 Copilot等）"),
                    opt("agents", "Agents with access to tools, APIs or databases", "ツール・API・データベースにアクセスするAIエージェント"),
                    opt("rag", "RAG systems searching internal documents and knowledge bases", "社内文書・ナレッジを検索するRAGシステム"),
                    opt("api", "External model APIs embedded in your products and services", "自社製品・サービスに組み込まれた外部モデルAPI"),
                    opt("sensitive", "AI systems process confidential or personal information", "AIが機密情報や個人情報を処理している"),
                    opt("logged", "Prompts and tool calls are logged and managed", "プロンプトやツール呼び出しのログを保存・管理している"),
                    opt("none", "No use or development of AI systems", "AIシステムの利用・開発なし"),
                    opt("other", "Other", "その他"),
                    opt("private", "Prefer not to say", "回答を控える")
                ]
            }
        ]
    },
    {
        "key": "posture",
        "title": {"en": "Security posture & test scope", "ja": "セキュリティ体制と診断範囲"},
        "blurb": {
            "en": "Your organization's current security operations, and the scope of testing you are considering. Please select all that apply.",
            "ja": "貴組織の現在のセキュリティ運用体制と、今回ご検討されている診断対象の範囲についてお伺いします。該当する項目をご選択ください。"
        },
        "questions": [
            {
                "key": "ownership",
                "type": "chips",
                "label": {"en": "Internal security ownership", "ja": "社内のセキュリティ責任体制"},
                "options": [
                    opt("ciso", "CISO", "CISO"),
                    opt("cto", "CTO / CIO", "CTO／CIO"),
                    opt("manager", "Dedicated security manager", "専任のセキュリティ責任者"),
                    opt("dpo", "Data protection or privacy officer", "個人情報保護責任者"),
                    opt("it", "Information systems / IT department, alongside other responsibilities", "情報システム・IT部門が他業務と兼務"),
                    opt("outsourced", "Outsourced to a service provider", "外部サービス事業者へ委託"),
                    opt("none", "No formally assigned owner", "責任者が明確に定められていない"),
                    opt("private", "Prefer not to say", "回答を控える")
                ]
            },
            {
                "key": "operations",
                "type": "selects",
                "label": {"en": "Security operations", "ja": "セキュリティ運用"},
                "fields": [
                    {"key": "who", "label": {"en": "Operated by", "ja": "運用主体"},
                        "options": [
                            opt("internal", "Internal team", "社内チーム"),
                            opt("domestic", "Domestic provider or MSSP", "国内事業者・MSSP"),
                            opt("overseas", "Overseas provider", "海外事業者"),
                            opt("mixed", "Combination of internal and external", "社内および外部事業者の併用"),
                            opt("none", "No designated party", "特に定めていない")
                        ]},
                    {"key": "hours", "label": {"en": "Monitoring", "ja": "監視体制"},
                        "options": [
                            opt("24x7", "24/7", "24時間365日"),
                            opt("business", "Business hours", "営業時間内"),
                            opt("onreport", "Upon report of an issue only", "事象の報告があった場合のみ"),
                            opt("none", "No formal monitoring in place", "定常的な監視は実施していない")
                        ]},
                    {"key": "staff", "label": {"en": "Dedicated security personnel", "ja": "セキュリティ専任要員"},
                        "options": [
                            opt("0", "None", "専任なし"),
                            opt("1-2", "1–2", "1〜2名"),
                            opt("3-10", "3–10", "3〜10名"),
                            opt("10+", "More than 10", "10名超")
                        ]}
                ]
            },
            {
                "key": "controls",
                "type": "chips",
                "label": {"en": "Security controls in place", "ja": "導入済みのセキュリティ対策"},
                "options": [
                    opt("endpoint", "Endpoint security (antivirus, EDR)", "エンドポイントセキュリティ（アンチウイルス・EDR）"),
                    opt("firewall", "Network firewall / UTM", "ファイアウォール / UTM"),
                    opt("waf", "WAF (web application firewall)", "WAF（Webアプリケーションファイアウォール）"),
                    opt("email", "Email security (spam filtering, gateway)", "メールセキュリティ（迷惑メールフィルタ、ゲートウェイ等）"),
                    opt("2fa", "Multi-factor authentication (MFA / two-factor) implemented or required", "多要素認証（MFA・二要素認証）の導入・必須化"),
                    opt("passwords", "Company password manager or vault", "法人向けパスワード管理ツール"),
                    opt("logging", "Centralized logging and monitoring (SIEM)", "ログの集約・監視（SIEM等の導入）"),
                    opt("managed", "Outsourced security monitoring and operations (SOC, MSSP)", "セキュリティ監視・運用の外部サービス（SOC、MSSP等）"),
                    opt("vuln", "Vulnerability scanning and patch / automatic update management", "脆弱性スキャンおよびパッチ（自動アップデート）管理"),
                    opt("privileged", "Strict control of administrator and privileged accounts (PAM)", "管理者・特権アカウントの厳格な権限管理（PAM等）"),
                    opt("dlp", "DLP (data loss prevention tooling)", "DLP（情報漏えい対策ツールの導入）"),
                    opt("cloudconfig", "Cloud configuration monitoring (CSPM)", "クラウド設定の監視（CSPM等）"),
                    opt("backup", "Regular backups and an established recovery capability", "バックアップの定期取得および復旧体制の整備"),
                    opt("appsec", "Regular application security assessments", "定期的なアプリケーションセキュリティ診断の実施"),
                    opt("awareness", "Phishing simulation and employee security training", "標的型攻撃訓練・従業員向けセキュリティ教育の実施"),
                    opt("none", "None of these", "該当なし"),
                    opt("other", "Other", "その他"),
                    opt("private", "Prefer not to say", "回答を控える")
                ]
            },
            {
                "key": "policies",
                "type": "radio",
                "label": {"en": "Security policies and procedures", "ja": "セキュリティ規程・手順"},
                "hint": {"en": "Covering incident response, access control, backup, patch management, and joiner and leaver procedures.",
                    "ja": "インシデント対応、アクセス管理、バックアップ、パッチ適用、入退社時の手続き等を対象とします。"},
                "options": [
                    opt("tested", "Documented and regularly tested", "文書化のうえ、定期的に検証・訓練を実施"),
                    opt("documented", "Documented but not tested", "文書化済み、検証は未実施"),
                    opt("informal", "Established in practice but not documented", "運用実態はあるが文書化されていない"),
                    opt("none", "Not yet established", "未整備"),
                    opt("unknown", "Unknown", "不明"),
                    opt("private", "Prefer not to say", "回答を控える")
                ]
            },
            {
                "key": "testing",
                "type": "chips",
                "label": {"en": "Prior security testing", "ja": "これまでに実施した診断"},
                "options": [
                    opt("none", "No prior testing", "実施実績なし"),
                    opt("scan", "Automated vulnerability scanning", "自動脆弱性スキャン"),
                    opt("external", "External penetration test", "外部ペネトレーションテスト"),
                    opt("internal", "Internal network penetration test", "内部ネットワーク診断"),
                    opt("webapp", "Web or mobile application assessment", "Web・モバイルアプリケーション診断"),
                    opt("cloud", "Cloud configuration review", "クラウド設定レビュー"),
                    opt("phishing", "Phishing or social engineering exercise", "フィッシング・ソーシャルエンジニアリング演習"),
                    opt("redteam", "Red team exercise", "レッドチーム演習"),
                    opt("ai", "AI or LLM security assessment", "AI・LLMセキュリティ診断"),
                    opt("recent", "An assessment of some kind carried out within the past 12 months", "直近12ヵ月以内にいずれかの診断を実施した"),
                    opt("other", "Other", "その他"),
                    opt("private", "Prefer not to say", "回答を控える")
                ]
            },
            {
                "key": "incidents",
                "type": "chips",
                "optional": true,
                "label": {"en": "Security incidents experienced", "ja": "過去のセキュリティインシデント"},
                "hint": {"en": "Optional at this stage, and treated as confidential.",
                    "ja": "本設問は任意です。ご回答内容は機密として取り扱います。"},
                "options": [
                    opt("none", "None known", "把握している限りなし"),
                    opt("ransomware", "Ransomware", "ランサムウェア"),
                    opt("bec", "Business email compromise", "ビジネスメール詐欺"),
                    opt("phishing", "Phishing / credential theft", "フィッシング・認証情報窃取"),
                    opt("cloud", "Cloud account compromise", "クラウドアカウント侵害"),
                    opt("exfil", "Data exfiltration or leakage", "データ持ち出し・漏えい"),
                    opt("insider", "Insider incident", "内部不正"),
                    opt("supplychain", "Supply-chain compromise (via a contractor or group company)", "サプライチェーン侵害（委託先やグループ会社経由の侵入）"),
                    opt("dos", "Denial of service (DDoS, server outage)", "サービス妨害（DDoS攻撃・サーバーダウン等）"),
                    opt("webapp", "Web application compromise (defacement, unauthorized access)", "Webアプリケーションへの侵害（改ざん・不正侵入等）"),
                    opt("misconfig", "Unintended disclosure through misconfiguration", "設定不備による意図しない情報公開"),
                    opt("private", "Prefer not to say", "回答を控える")
                ]
            },
            {
                "key": "targeted",
                "type": "chips",
                "optional": true,
                "label": {"en": "Indications of prior targeting", "ja": "標的とされた形跡の有無"},
                "hint": {"en": "Observed indicators that your organization has been the subject of attention, whether or not an attempt succeeded. Responses are treated in strict confidence.",
                    "ja": "攻撃の成否にかかわらず、貴組織が関心を向けられた形跡として観測された事象をお選びください。ご回答内容は厳格な機密として取り扱います。"},
                "options": [
                    opt("none", "None known", "把握している限りなし"),
                    opt("leaksite", "Named on, or threatened with, a ransomware leak site", "ランサムウェアのリークサイト（暴露サイト）への掲載・予告"),
                    opt("dumps", "Employee credentials (IDs, passwords) found in leaked data", "従業員の認証情報（ID・パスワード等）が流出データに含まれていた"),
                    opt("spearphish", "Targeted phishing emails received by executives or finance staff", "経営層や経理部門を狙った標的型フィッシングメールの受信"),
                    opt("statenotice", "Notification of state-sponsored activity from a vendor or authority", "国家関与の活動に関する事業者・当局からの通知"),
                    opt("impersonation", "Fake sites impersonating your brand, or impersonation accounts of executives", "自社ブランド（偽サイト等）や役員のなりすましアカウントの存在"),
                    opt("scanning", "Detection of persistent external scanning or repeated intrusion attempts", "外部からの継続的なスキャン・執拗な侵入試行の検知"),
                    opt("threats", "Threats from activists, competitors or insiders", "活動家・競合・内部関係者からの脅迫行為"),
                    opt("unsure", "May not be detectable or known to us", "自社では把握・検知できていない可能性がある"),
                    opt("private", "Prefer not to say", "回答を控える")
                ]
            },
            {
                "key": "scope",
                "type": "chips",
                "label": {"en": "Surfaces to be assessed", "ja": "診断対象とする領域"},
                "hint": {"en": "Select the areas you would like assessed. If this is not yet determined, choose \u300cNot yet determined\u300d and we will propose the most appropriate scope based on our discussion with you.",
                    "ja": "診断をご希望される領域をお選びください。未確定の場合は「未定」をお選びいただければ、ヒアリング内容をもとに最適な範囲を当社よりご提案します。"},
                "options": [
                    opt("external", "External perimeter", "外部公開資産"),
                    opt("internal", "Internal network", "内部ネットワーク"),
                    opt("cloud", "Cloud configuration", "クラウド構成"),
                    opt("webapp", "Web / mobile applications", "Web・モバイルアプリケーション"),
                    opt("identity", "Identity and directory", "ID基盤・ディレクトリ"),
                    opt("people", "Personnel (phishing simulation, social engineering)", "人的要素（フィッシングメール訓練・ソーシャルエンジニアリング等）"),
                    opt("ai", "AI agents and LLM applications", "AIエージェント・LLMアプリケーション"),
                    opt("ot", "OT / IoT", "OT・IoT"),
                    opt("physical", "Physical access", "物理セキュリティ"),
                    opt("other", "Other", "その他"),
                    optd("advise", "Not yet determined", "未定",
                        "We will propose a scope based on your responses.",
                        "ご回答内容にもとづき、当社より診断範囲をご提案します。")
                ]
            },
            {
                "key": "scenarios",
                "type": "chips",
                "label": {"en": "Specific security testing of interest", "ja": "ご関心のある具体的な診断項目"},
                "hint": {"en": "We will also propose our own recommendations — the simulations we would suggest — based on your responses.",
                    "ja": "ご回答内容に基づいた当社からの推奨事項（おすすめのシミュレーション）もご提案いたします。"},
                "options": [
                    opt("ransomware", "Ransomware simulation (encryption and extortion scenario)", "ランサムウェアシミュレーション（暗号化・脅迫シナリオ）"),
                    opt("phishing", "Phishing resilience testing (suspicious email)", "フィッシング攻撃への耐性検証（不審メール等）"),
                    opt("social", "Social engineering (intrusion testing that exploits human psychology)", "ソーシャルエンジニアリング（人の心理を突いた侵入テスト）"),
                    opt("exfil", "Data exfiltration and leakage path testing", "データの持ち出し・漏えい経路の検証（エクスフィルトレーション）"),
                    opt("promptinjection", "LLM prompt injection (malicious instructions to AI)", "LLMプロンプトインジェクション（AIへの不正指示攻撃）"),
                    opt("credentials", "Credential attacks (password brute-force and capture testing)", "認証情報への攻撃（パスワード総当たり・奪取テスト）"),
                    opt("cloudbreak", "Cloud privilege escalation (administrator takeover via misconfiguration)", "クラウド権限昇格（設定不備を突いた管理者権限の奪取）"),
                    opt("supplychain", "Supply-chain intrusion simulation", "サプライチェーン経由の侵入シミュレーション"),
                    opt("webapi", "Web application and API vulnerability testing", "Webアプリケーション・APIの脆弱性検証"),
                    opt("other", "Other", "その他"),
                    optd("advise", "Not yet determined", "未定",
                        "We will propose a testing programme based on your responses.",
                        "ご回答内容にもとづき、当社より診断プログラムをご提案します。")
                ]
            },
            {
                "key": "timeline",
                "type": "select",
                "label": {"en": "Anticipated timeline", "ja": "実施をご希望される時期"},
                "options": [
                    opt("asap", "As soon as possible", "できるだけ早く"),
                    opt("1-3m", "Within 1–3 months", "1〜3ヵ月以内"),
                    opt("3-6m", "Within 3–6 months", "3〜6ヵ月以内"),
                    opt("exploring", "Currently evaluating options", "現時点では情報収集段階"),
                    opt("private", "Prefer not to say", "回答を控える")
                ]
            }
        ]
    }
];

const CLOSING = {
    "notes": {
        "label": {"en": "Additional information", "ja": "補足事項"},
        "hint": {"en": "Constraints, deadlines, particular concerns, or systems that must remain out of scope.",
            "ja": "制約条件、ご希望期限、特にご懸念のある事項、診断対象外とすべきシステム等がございましたらご記入ください。"}
    },
    "consent": {
        "label": {
            "en": "I understand that no testing begins until scope and written authorization are agreed and signed.",
            "ja": "診断は、対象範囲の合意と書面による実施許可の取得後にのみ開始されることを理解しています。"
        }
    }
};

export {PAGES, CLOSING};
